/**
 * The geometry a classification is allowed to be based on.
 *
 * Every threshold here is a hypothesis. They were chosen from the reasoning in the 4C design and from what the PD gates settled at, not from a study of real faces, and 4C.2 reports what they accept and refuse rather than claiming they are right. They must never be nudged to make a synthetic case land on its expected label — a gate that moves to fit the answer has stopped being a gate.
 *
 * The reason they exist at all is that the features are scale-free only under near-frontal capture. Yaw foreshortens one half of the outline, pitch shortens the apparent length, a cropped chin truncates the profile, and too few pixels make the outline noise. None of those is correctable after the fact, so each is refused.
 */
import type { Outline } from './oval';
import type { WidthProfile } from './profile';
import { faceHeightPx } from './features';

export interface GateConfig {
  /** Head rotation, degrees. Yaw is the tightest because it distorts the silhouette directly and cannot be undone in 2D. */
  maxYawDeg: number;
  maxPitchDeg: number;
  maxRollDeg: number;
  /** Pole-to-pole height in pixels, below which the outline is mostly quantisation. */
  minFaceHeightPx: number;
  /** Mean left-right half-width disagreement, as a share of the widest chord. */
  maxAsymmetry: number;
  /** How close the outline may come to the frame edge, as a share of the frame's smaller side. */
  minMarginShare: number;
}

/** The starting values. Provisional: see the module note. */
export const GATES: GateConfig = {
  maxYawDeg: 3,
  maxPitchDeg: 6,
  maxRollDeg: 8,
  minFaceHeightPx: 260,
  maxAsymmetry: 0.04,
  minMarginShare: 0.04,
};

/** Why the geometry was refused. Codes only; they are never shown to a customer as they stand. */
export const GateFailure = {
  NO_FACE: 'NO_FACE',
  MULTIPLE_FACES: 'MULTIPLE_FACES',
  INCOMPLETE_OUTLINE: 'INCOMPLETE_OUTLINE',
  NOT_MEASURABLE: 'NOT_MEASURABLE',
  EXCESSIVE_YAW: 'EXCESSIVE_YAW',
  EXCESSIVE_PITCH: 'EXCESSIVE_PITCH',
  EXCESSIVE_ROLL: 'EXCESSIVE_ROLL',
  FACE_TOO_SMALL: 'FACE_TOO_SMALL',
  ASYMMETRIC_OUTLINE: 'ASYMMETRIC_OUTLINE',
  FACE_CROPPED: 'FACE_CROPPED',
} as const;
export type GateFailure = (typeof GateFailure)[keyof typeof GateFailure];

export interface GeometryQuality {
  /** Every gate the geometry failed, in the order checked. Empty means it passed. */
  failures: GateFailure[];
  /** What each gate actually measured, for the report and for tuning later. */
  measured: {
    yawDeg: number | null;
    pitchDeg: number | null;
    rollAppliedDeg: number;
    faceHeightPx: number;
    asymmetry: number;
    marginShare: number;
  };
}

export interface GateInput {
  outline: Outline;
  profile: WidthProfile;
  measurable: boolean;
  rollAppliedDeg: number;
  yawDeg: number | null;
  pitchDeg: number | null;
  frameWidth: number;
  frameHeight: number;
}

/** Mean disagreement between the two half-widths, over the widest chord. */
export function asymmetryOf(profile: WidthProfile): number {
  let sum = 0;
  for (let i = 0; i < profile.left.length; i += 1) {
    sum += Math.abs(profile.left[i]! - profile.right[i]!);
  }
  return profile.maxWidthPx === 0 ? 1 : sum / profile.left.length / profile.maxWidthPx;
}

/** How close the outline comes to the nearest frame edge, as a share of the frame's smaller side. */
export function marginShareOf(outline: Outline, frameWidth: number, frameHeight: number): number {
  let nearest = Infinity;
  for (const p of outline) {
    nearest = Math.min(nearest, p.x, p.y, frameWidth - p.x, frameHeight - p.y);
  }
  return nearest / Math.min(frameWidth, frameHeight);
}

export function checkGeometry(input: GateInput, gates: GateConfig = GATES): GeometryQuality {
  const asymmetry = asymmetryOf(input.profile);
  const marginShare = marginShareOf(input.outline, input.frameWidth, input.frameHeight);
  const height = faceHeightPx(input.outline);

  const failures: GateFailure[] = [];
  if (!input.measurable) failures.push(GateFailure.NOT_MEASURABLE);
  if (input.yawDeg !== null && Math.abs(input.yawDeg) > gates.maxYawDeg) {
    failures.push(GateFailure.EXCESSIVE_YAW);
  }
  if (input.pitchDeg !== null && Math.abs(input.pitchDeg) > gates.maxPitchDeg) {
    failures.push(GateFailure.EXCESSIVE_PITCH);
  }
  if (Math.abs(input.rollAppliedDeg) > gates.maxRollDeg) failures.push(GateFailure.EXCESSIVE_ROLL);
  if (height < gates.minFaceHeightPx) failures.push(GateFailure.FACE_TOO_SMALL);
  if (asymmetry > gates.maxAsymmetry) failures.push(GateFailure.ASYMMETRIC_OUTLINE);
  if (marginShare < gates.minMarginShare) failures.push(GateFailure.FACE_CROPPED);

  return {
    failures,
    measured: {
      yawDeg: input.yawDeg,
      pitchDeg: input.pitchDeg,
      rollAppliedDeg: input.rollAppliedDeg,
      faceHeightPx: height,
      asymmetry,
      marginShare,
    },
  };
}
